<script setup lang="ts">
  // @ts-nocheck
  import {
    computed,
    onMounted,
    ref,
    watch,
    inject,
    defineAsyncComponent,
    type ComputedRef,
  } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useRoute } from 'vue-router'
  import FlowCard from '@/shared/components/FlowCard/FlowCard.vue'
  import { useCommonStore, useQuoteStore, useTravelersStore, useUserStore } from '@/shared/store'
  import {
    LOAD_EVENT_NAME,
    PROCEED_EVENT_NAME,
    PAYMENT_REDIRECT_HASH,
  } from './FlowQuoteWidget.constants'
  import QuoteClaimDocsPopup from './components/QuoteClaimDocsPopup'
  import QuotePaymentTermsPopup from './components/QuotePaymentTermsPopup'
  import { BaseButton, formatDate, ToggleInput, VLoader } from '@qic/ui-components'
  import {
    type GetTravelQuoteUaeResponse,
    type PaymentOption,
    type Country,
    CountryBoundType,
    PromoCodeTypes,
    WordingDocName,
  } from '@/shared/types/api.types'
  import AlertContainer from '@/shared/components/AlertContainer/AlertContainer.vue'
  import BaseFoldingBlock from '@/shared/components/BaseFoldingBlock/BaseFoldingBlock.vue'
  import InformationCard from '@/shared/components/InformationCard/InformationCard.vue'
  import TravelQuoteIcon from '@/shared/components/icons/TravelQuoteIcon.vue'
  import BaseChip from '@/shared/components/BaseChip/BaseChip.vue'
  import FlowAccordion from '@/entities/Flow/components/FlowAccordion/FlowAccordion.vue'
  import PaymentOptions from '@/entities/Flow/components/PaymentOptions'
  import ApplePayBtn from '@/entities/Flow/components/ApplePayBtn'
  import PencilIcon from '@/shared/components/icons/PencilIcon.vue'
  import BaseCheckbox from '@/shared/components/BaseCheckbox/BaseCheckbox.vue'
  import BaseReminder from '@/shared/components/BaseReminder/BaseReminder.vue'
  import TicketBlock from '@/shared/components/TicketBlock/TicketBlock.vue'
  import { useDebounceFn } from '@vueuse/core'
  import { TravelDirection } from '@/shared/types/common.types'
  import { useWinterSport, useGoogleAnalytics, usePerformanceTiming, useLinkTarget } from '@/shared/composables'
  import { formatEmiratesId } from '@/shared/utils/EmiratesId'
  import {
    COVID_ADDON_ID,
    CYBER_ADDON_ID,
    WINTER_SPORTS_ADDON_ID,
  } from '@/shared/constants/addons.constants'
  import { parseDateFromStr, toFixedNumber } from '@/shared/utils'
  import {
    OUTBOUND_MULTI_TRIP_RETURN_DAY,
    PLAN_NAMES_HASH,
    PLAN_NAMES_SCHEME_TYPE_CODE_MAP,
  } from '@/shared/constants/plans.constants'
  import { APPLE_PAY_PAYMENT_OPTIONS } from '@/shared/constants/api.constants'
  import CoversBlock from './components/CoversBlock'
  import FingerprintLottie from '@/shared/components/lottie/FingerprintLottie.vue'
  import AddonTooltip from '@/shared/components/AddonTooltip/AddonTooltip.vue'
  import CyberSecurityButton from '@/entities/Flow/components/CyberSecurityModal/CyberSecurityButton.vue'
  import InformationCardInsuranceValues from '@/shared/components/InformationCard/InformationCardInsuranceValues.vue'
  import { INSURANCE_VALUES_SP_TEST_VALUES } from '@/views/Flow/FlowSelectPlanView/FlowSelectPlanView.constants'
  import {
    INSURENCE_VALUES_MULTI_PLAN_TEST_KEY,
    INSURENCE_VALUES_SINGLE_PLAN_TEST_KEY,
    AIRALO_TEST_KEY,
    AIRALO_TEST_VALUES,
  } from '@/shared/constants'
  import { useAbTest } from '@/shared/composables/useAbTest'
  import FlowQuoteCoverage from '@/entities/Flow/components/FlowQuoteCoverage/FlowQuoteCoverage.vue'
  import { isAppleOS } from '@/shared/utils/ApplePay'
  import AiraloBanner from './components/AiraloBanner/AiraloBanner.vue'
  import { useFormDataWithRedirect } from '@/shared/composables/useFormDataWithRedirect'
  import { PreferredCoinsReward } from '@/shared/store/userStore'
  import OoredooBanner from './components/OoredooBanner/OoredooBanner.vue'
  import { useFeaturesStore } from '@/shared/store/featuresStore'
  import type { AvailableFlowRoutes } from '@/shared/types/flow.types'
  import { InboundFlowRoutes, OutboundFlowRoutes } from '@/shared/types/router.types'

  const props = withDefaults(defineProps<Props>(), {
    typeOfTravel: TravelDirection.OUTBOUND,
  })
  const emit = defineEmits<Emits>()
  const CoinsRewardOptions = defineAsyncComponent(() =>
    import('./components/CoinsRewardOptions').then((m) => m.CoinsRewardOptions)
  )
  const CoinsReward = defineAsyncComponent(() =>
    import('./components/CoinsRewards').then((m) => m.CoinsReward)
  )
  const CoinsDiscount = defineAsyncComponent(() =>
    import('./components/CoinsRewards').then((m) => m.CoinsDiscount)
  )

  // TODO: REFACTOR IT
  interface Props {
    typeOfTravel?: TravelDirection
  }

  interface Emits {
    (e: typeof PROCEED_EVENT_NAME, nextStep?: AvailableFlowRoutes): void

    (
      e: typeof LOAD_EVENT_NAME,
      request: () => Promise<any>,
      requestId?: string,
      withLoader?: boolean,
      withError?: boolean,
      infinityLoader?: boolean
    ): void

    (e: 'edit-travelers'): void

    (e: 'toggle-addon', addonId: string, isChecked: boolean): void
  }

  const route = useRoute()
  const { getFormData } = useFormDataWithRedirect()
  const userStore = useUserStore()
  const commonStore = useCommonStore()
  const quoteStore = useQuoteStore()
  const travelersStore = useTravelersStore()
  const featuresStore = useFeaturesStore()
  const { withWinterSport } = useWinterSport()
  const { getAbTest } = useAbTest()
  const { t, locale } = useI18n()
  const quote = ref<GetTravelQuoteUaeResponse | null>(null)
  const paymentOptions = ref<PaymentOption[]>([])
  const selectedPaymentOption = ref('')
  const travelers = ref([])
  const totalPrice = ref(0)
  const listOfDestionations = ref<Country[]>([])
  const termConditionsLink = ref('#')
  const gaService = useGoogleAnalytics()
  const { getLinkTarget } = useLinkTarget()
  const { onPerformanceCheckEnd } = usePerformanceTiming()
  const numberAddons = ref([])
  const isInsuranceValuesQuote = ref(false)
  const isRamadanCampaign = inject('isRamadanCampaign') as ComputedRef<boolean>
  const alertContainerRef = ref<InstanceType<typeof AlertContainer> | null>(null)

  const isOutbound = computed(() => props.typeOfTravel === TravelDirection.OUTBOUND)
  const covidAddon = computed(
    () => selectedPlan.value?.ids.find((addon) => addon.id === COVID_ADDON_ID) ?? null
  )
  const withCovid = ref(userStore.withCovid)
  const winterSportExtension = computed(
    () => selectedPlan.value?.ids.find((addon) => addon.id === WINTER_SPORTS_ADDON_ID) ?? {}
  )

  const withCyber = ref(userStore.withCyber)
  const cyberAddon = computed(
    () => selectedPlan.value?.ids.find((addon) => addon.id === CYBER_ADDON_ID) ?? null
  )

  const hasApplePayOption = computed(() =>
    paymentOptions.value.some((o) => APPLE_PAY_PAYMENT_OPTIONS.includes(o.paymtType))
  )

  const isApplePaySelected = computed(() =>
    APPLE_PAY_PAYMENT_OPTIONS.includes(selectedPaymentOption.value)
  )

  const showAiraloBanner = computed(
    () =>
      AIRALO_TEST_VALUES.includes(commonStore.getAbValue(AIRALO_TEST_KEY)) &&
      isOutbound.value &&
      featuresStore.isAiralooEnabled
  )

  const abTestKey = computed(() =>
    quote.value?.schemes?.length > 1
      ? INSURENCE_VALUES_MULTI_PLAN_TEST_KEY
      : INSURENCE_VALUES_SINGLE_PLAN_TEST_KEY
  )

  const selectedPlan = computed(() =>
    quote.value?.schemes.find((scheme) => scheme.schemecode == userStore.selectedPlan)
  )

  const canApplyCoinsDiscount = computed(
    () => !!quoteStore.quoteWithCoinsDiscount && !!selectedPlan.value?.coinsDiscountValue
  )

  const shouldApplyCoinsDiscount = computed(
    () => canApplyCoinsDiscount.value && userStore.preferredReward === PreferredCoinsReward.SPEND
  )

  watch(canApplyCoinsDiscount, (newVal) => {
    if (newVal) {
      userStore.setPreferredReward(PreferredCoinsReward.SPEND, true)
    } else {
      userStore.setPreferredReward(PreferredCoinsReward.EARN, true)
    }
  })

  const getTravelQuoteUae = async () => {
    const destinationIds = isOutbound.value
      ? userStore.destination.map((country) => country?.id)
      : []
    if (isOutbound.value && destinationIds.length === 0) {
      return Promise.reject('Destination list is empty. Did you skip the first step?')
    }

    let type: PromoCodeTypes | undefined

    if (isOutbound.value && userStore.isAffinity && userStore.promocode) {
      type = PromoCodeTypes.AFFINITY
    }

    quote.value = await quoteStore.getTravelQuoteUae(
      userStore.promocode ?? '',
      1,
      destinationIds,
      type
    )

    quoteStore.setQuoteNo(quote.value.quoteno, true)
    quoteStore.setQuoteWithCoinsDiscount(quote.value.quoteWithCoinsDiscount, true)
    quoteStore.setCoinsDiscountPrice(quote.value.coinsDiscountPrice, true)
    if (quote.value.quoteWithCoinsDiscount) {
      userStore.setPreferredReward(PreferredCoinsReward.EARN, true)
    }
  }

  const getTravelers = async () => {
    const response = await travelersStore.getTravelers()

    travelers.value = [
      {
        srNo: 'holder',
        firstName: userStore.firstName,
        lastName: userStore.familyName,
        dateOfBirth: userStore.dateOfBirth,
        relationCode: undefined,
        relationName: undefined,
        nationalityCode: userStore.nationality.nationalityCode,
        nationalityName: userStore.nationality.nationalityName,
        passportNo: userStore.passportNumber,
        winSports: withWinterSport.value && isOutbound.value ? userStore.withWinterSport : false,
        emirateId: userStore.emiratesId ? formatEmiratesId(userStore.emiratesId) : null,
        civilIdExpiryDate: userStore.civilIdExpiryDate,
        gender:
          userStore.gender === '1'
            ? t('outbound.travelers.gender.male')
            : t('outbound.travelers.gender.female'),
      },
      ...response.map((traveler) => ({
        ...traveler,
        dateOfBirth: parseDateFromStr(traveler.dateOfBirth),
        winSports: withWinterSport.value && isOutbound.value ? !!traveler.winSports : false,
      })),
    ]
    quoteStore.setInsuredTravelers(travelers.value.length, true)
  }

  const shouldDisplayAppleButton = (paymentType: string) =>
    APPLE_PAY_PAYMENT_OPTIONS.includes(paymentType) && isAppleOS()

  const getPaymentOptions = async () => {
    let quoteNo = quote.value?.quoteno

    if (quoteNo) {
      paymentOptions.value = await commonStore
        .getPaymentOptions(quoteNo, locale.value)
        .then((res) =>
          res.sort((a, b) => {
            if (shouldDisplayAppleButton(a.paymtType)) {
              return -1
            }

            return 0
          })
        )

      setDefaultPaymentOption(paymentOptions.value)
    }
  }

  const setDefaultPaymentOption = (options: PaymentOption[]) => {
    let resultOption = ''

    if (options.length) {
      resultOption = options[0].paymtType
    }

    for (const opt of options) {
      if (opt.paymtType === 'CC') {
        resultOption = opt.paymtType
      }

      if (shouldDisplayAppleButton(opt.paymtType)) {
        resultOption = opt.paymtType
        break
      }
    }

    selectedPaymentOption.value = resultOption
  }

  const getTotalPrice = async (selectProduct: 0 | 1 = 0) => {
    const payload = {
      quoteno: quote.value?.quoteno,
      schemecode: selectedPlan.value?.schemecode,
      productcode: selectedPlan.value?.productcode,
      commonAddons: [],
      personalAddons: [],
      selectproduct: selectProduct,
    }

    if (shouldApplyCoinsDiscount.value) {
      payload.quoteWithCoinsDiscount = quoteStore.quoteWithCoinsDiscount
    }

    if (isOutbound.value) {
      if (covidAddon.value) {
        payload.commonAddons.push({
          id: COVID_ADDON_ID,
          isActive: withCovid.value,
        })
      }

      for (const traveler of travelers.value) {
        payload.personalAddons.push({
          srNo: traveler.srNo === 'holder' ? 0 : traveler.srNo,
          addons: [
            {
              id: WINTER_SPORTS_ADDON_ID,
              isActive: traveler.winSports,
            },
          ],
        })
      }
    }

    if (cyberAddon.value) {
      payload.commonAddons.push({
        id: CYBER_ADDON_ID,
        isActive: withCyber.value,
      })
    }

    const response = await quoteStore.getTravelTotalPrice(payload)
    totalPrice.value = response.totalprice
    quoteStore.setProductBonus(response.totalBonus ?? 0, true)
  }

  const getFinalQuotePrice = getTotalPrice.bind(null, 1)

  const getWordings = async () => {
    const response = await commonStore.getQatarWordings(
      7,
      props.typeOfTravel === TravelDirection.OUTBOUND
        ? WordingDocName.POLICY_WORDING
        : WordingDocName.POLICY_WORDING_INBOUND
    )
    termConditionsLink.value = response[0].Link
  }

  const getDestinations = async () => {
    const boundType = TravelDirection.OUTBOUND ? CountryBoundType.OUTBUND : CountryBoundType.INBUND
    listOfDestionations.value = await userStore.fetchCountries(boundType, locale.value)
  }

  const informationCardTitle = computed(() => {
    if (isOutbound.value) {
      if (!selectedPlan.value) {
        return ''
      }

      const translateKey = isInsuranceValuesQuote.value
        ? PLAN_NAMES_SCHEME_TYPE_CODE_MAP[selectedPlan.value.schemeTypeCode]
        : PLAN_NAMES_HASH[selectedPlan.value.schemecode]
      return translateKey ? t(translateKey) : selectedPlan.value.name
    }

    return t('quote_info_inbound_to_qatar')
  })

  const destinationText = computed(() => {
    if (isOutbound.value) {
      const ids = userStore.destination.map((el) => el.id)
      return listOfDestionations.value
        .filter((el) => ids.includes(el.id))
        .map((el) => el.name)
        .join(', ')
    }

    const id = userStore.residenceCountry?.id
    return listOfDestionations.value.find((el) => el.id === id)?.name || ''
  })

  const informationCardOptions = computed(() => {
    let returnDate = userStore.returnDate as Date
    if (userStore.isOneYearPolicySelected) {
      returnDate = new Date(
        new Date(userStore.departureDate).setDate(
          userStore.departureDate.getDate() + OUTBOUND_MULTI_TRIP_RETURN_DAY
        )
      )
    }

    const res = [
      {
        title: t('check_quote.travel_dates'),
        content: `${formatDate(userStore.departureDate as Date, locale.value)} - ${formatDate(
          returnDate,
          locale.value
        )}`,
      },
    ]

    if (isInsuranceValuesQuote.value) {
      res.push({
        title: 'travelers',
        content: t(
          'outbound.select_plan.insurance_values.travelers',
          { travelersCount },
          travelersCount
        ),
      })
    } else {
      res.push(
        {
          title: isOutbound.value ? t('qic_quote_destination') : t('quote_country_residence'),
          content: destinationText.value,
        },
        {
          title: t('qic_quote_number'),
          content: quote.value?.quoteno,
        }
      )
    }

    return res
  })

  const travelersCountText = computed(
    () =>
      `${t(
        'outbound.plans.policy_information.people',
        { personCount: travelers.value.length },
        travelers.value.length
      )}`
  )

  const winterSportExtensionPrice = computed(
    () => winterSportExtension.value.value / travelers.value.length
  )

  const onEditTravelers = () => {
    const ids = userStore.destination.map((el) => el.id)

    gaService.sendEditTravellersQuote({
      category: props.typeOfTravel,
      numOfTravelers: travelersCount,
      quoteId: quote.value?.quoteno || 0,
      totalPrice: totalPrice.value,
      countries: listOfDestionations.value.filter((el) => ids.includes(el.id)).map((el) => el.name),
    })

    emit('edit-travelers')
  }

  const addonsPrice = computed(() => {
    let price = 0
    if (withCovid.value && covidAddon.value) price += covidAddon.value.value

    price +=
      Object.values(travelers.value).filter(
        (traveler) => traveler.winSports && withWinterSport.value
      ).length * winterSportExtensionPrice.value
    return Math.round(price)
  })

  const travelersWithWinterSport = computed(() =>
    Object.values(travelers.value).filter((traveler) => traveler.winSports && withWinterSport.value)
  )

  const winterSportPrice = computed(() => {
    const price = travelersWithWinterSport.value.length
      ? travelersWithWinterSport.value.length * winterSportExtensionPrice.value
      : winterSportExtensionPrice.value

    return Math.round(price)
  })

  const winterSportPriceText = computed(() =>
    travelersWithWinterSport.value.length
      ? t(
          'check_quote.people',
          { count: travelersWithWinterSport.value.length },
          travelersWithWinterSport.value.length
        )
      : t('check_quote.person')
  )

  const isPriceLoading = ref(false)

  const activeAddonsCount = computed(() => {
    const covidCount = withCovid.value && covidAddon.value ? 1 : 0
    const travelersWinterSportCount = travelers.value.filter(
      (traveler) => traveler.winSports && withWinterSport.value
    ).length

    return travelersWinterSportCount + covidCount
  })

  const setAddonsPrice = () => {
    quoteStore.setAddonsPrice(addonsPrice.value, true)
  }

  const setActiveAddons = () => {
    quoteStore.setActiveAddons(activeAddonsCount.value, true)
  }

  const onCovidChange = (value: boolean) => {
    withCovid.value = value
    userStore.setwithCovid(withCovid.value, true)
    if (covidAddon.value) {
      onToggleAddon(covidAddon.value.id, withCovid.value, 'holder')
    }
  }

  const onCyberChange = (value: boolean) => {
    withCyber.value = value
    userStore.setWithCyber(withCyber.value, true)
    if (cyberAddon.value) {
      onToggleAddon(cyberAddon.value.id, withCyber.value, 'holder')
    }
  }

  const onToggleAddon = useDebounceFn(async (addonId: string, isChecked: boolean, srNo: string) => {
    try {
      isPriceLoading.value = true

      if (addonId === WINTER_SPORTS_ADDON_ID) {
        travelers.value = travelers.value.map((t) => {
          if (t.srNo === srNo) {
            t.winSports = isChecked
          }

          return t
        })

        if (srNo === 'holder') {
          userStore.setWithWinterSport(isChecked, true)
          gaService.sendSwitchAddon({
            addon_name: 'winter sport',
            quote_id: quote.value?.quoteno,
            addon_price: String(toFixedNumber(winterSportPrice.value)),
            state: isChecked,
            person_name: `${userStore.firstName} ${userStore.familyName}`,
            index: '0',
            num_of_travelers: travelers.value.length,
          })
        } else {
          const traveler = travelers.value.find((traveler) => traveler.srNo === srNo)
          if (!traveler) return

          gaService.sendSwitchAddon({
            addon_name: 'winter sport',
            quote_id: quote.value?.quoteno,
            addon_price: String(toFixedNumber(winterSportPrice.value)),
            state: isChecked,
            person_name: `${traveler.firstName} ${traveler.lastName}`,
            index: srNo,
            num_of_travelers: travelers.value.length,
          })
        }
      } else if (addonId === COVID_ADDON_ID) {
        gaService.sendSwitchAddon({
          addon_name: 'COVID-19',
          quote_id: quote.value?.quoteno,
          addon_price: String(toFixedNumber(isChecked ? covidAddon.value.value : 0)),
          state: isChecked,
        })
      } else {
        gaService.sendSwitchAddon({
          addon_name: 'cyber_security',
          quote_id: quote.value?.quoteno,
          addon_price: toFixedNumber(isChecked && cyberAddon.value ? cyberAddon.value.value : 0),
          state: isChecked,
          category: props.typeOfTravel,
        })
      }

      setAddonsPrice()
      setActiveAddons()
      setAddonsList()

      await getTotalPrice()
    } catch {
      onToggleAddonError(addonId, isChecked, srNo)
    } finally {
      isPriceLoading.value = false
    }
  }, 100)

  const onToggleAddonError = (addonId: string, isChecked: boolean, srNo: string) => {
    const prevValue = !isChecked

    alertContainerRef.value?.add(t('outbound.addon.error'), 5000)

    if (addonId === WINTER_SPORTS_ADDON_ID) {
      if (srNo === 'holder') {
        userStore.setWithWinterSport(prevValue, true)
      }

      travelers.value = travelers.value.map((traveler) => {
        if (traveler.srNo === srNo) {
          traveler.winSports = prevValue
        }

        return traveler
      })
    }

    if (addonId === COVID_ADDON_ID) {
      withCovid.value = prevValue
    }

    if (addonId === CYBER_ADDON_ID) {
      withCyber.value = prevValue
    }
  }

  const isTermsConditionsChecked = ref(true)
  const isPaymentTermsShown = ref(false)
  const isClaimDocumentsListShown = ref(false)

  const checkboxErrors = computed(() => ({
    agree: isTermsConditionsChecked.value ? null : t('qic.quote.agreeTerms'),
  }))

  const onPaymentError = () => {
    console.log('The selected payment option is not valid!')
  }

  const setPaymentMethod = () => {
    let paymentMethod = ''
    if (selectedPaymentOption.value === 'DC') {
      paymentMethod = 'naps'
    } else if (isApplePaySelected.value) {
      paymentMethod = 'apple pay'
    } else {
      paymentMethod = 'credit-card'
    }

    userStore.setPaymentMethod(paymentMethod, true)
  }

  const onBuy = async () => {
    if (!isTermsConditionsChecked.value || isPriceLoading.value) return

    await emit(
      LOAD_EVENT_NAME,
      async () => {
        const redirectUrl = PAYMENT_REDIRECT_HASH[selectedPaymentOption.value]
        if (!redirectUrl && !isApplePaySelected.value) {
          throw new Error('No payment selected!')
        }

        userStore.setLastFlowDirection(props.typeOfTravel, true)
        setPaymentMethod()
        quoteStore.setIsSentPurchaseAnalyticsEvent(false, true)

        if (props.typeOfTravel === TravelDirection.INBOUND) {
          gaService.sendQuoteBuy(
            userStore.promocode ?? '',
            quote.value.quoteno,
            travelers.value.length,
            totalPrice.value,
            [userStore.residenceCountry.name],
            userStore.paymentMethod
          )
        } else if (props.typeOfTravel === TravelDirection.OUTBOUND) {
          const coinsDiscountParams = canApplyCoinsDiscount.value
            ? {
                tabTitle: userStore.preferredReward,
                discountPrice:
                  userStore.preferredReward === PreferredCoinsReward.SPEND
                    ? toFixedNumber(selectedPlan.value?.coinsDiscountValue || 0)
                    : undefined,
              }
            : {}

          gaService.sendOutboundQuoteBuy(
            userStore.promocode ?? '',
            quote.value.quoteno,
            travelers.value.length,
            addonsPrice.value,
            totalPrice.value,
            quoteStore.addonsList,
            userStore.destination.map((country) => country.id),
            userStore.paymentMethod,
            quoteStore.activeAddons,
            coinsDiscountParams.tabTitle,
            coinsDiscountParams.discountPrice
          )
        }

        if (isApplePaySelected.value) {
          return emit(
            PROCEED_EVENT_NAME,
            isOutbound.value
              ? OutboundFlowRoutes.INSURANCE_RESULT
              : InboundFlowRoutes.INSURANCE_RESULT
          )
        } else {
          await getFinalQuotePrice()
        }

        window.location.href = redirectUrl
      },
      'payment',
      true,
      false,
      true
    )
  }

  const addonsList = ref([])
  const setAddonsList = () => {
    if (covidAddon.value) {
      if (withCovid.value && !addonsList.value.includes(covidAddon.value.name)) {
        addonsList.value.push(covidAddon.value.name)
      } else if (!withCovid.value) {
        addonsList.value = addonsList.value.filter((item) => item !== covidAddon.value.name)
      }
    }
    const winterSportCount = travelers.value.filter(
      (traveler) => traveler.winSports && withWinterSport.value
    ).length

    if (!winterSportCount) {
      addonsList.value = addonsList.value.filter((item) => item !== winterSportExtension.value.name)
    } else {
      if (!addonsList.value.includes(winterSportExtension.value.name)) {
        addonsList.value.push(winterSportExtension.value.name)
      }
    }

    quoteStore.setAddonsList(addonsList.value, true)
  }

  watch(
    () => totalPrice.value,
    (newValue) => {
      quoteStore.setTotalPrice(newValue, true)
    }
  )

  const onPaymentTermsClick = () => {
    isPaymentTermsShown.value = true
    gaService.sendPaymentTermsConditions(route.name, quote.value?.quoteno)
  }

  const paymentDescriptionForPopup = () =>
    paymentOptions.value.find((o) => o.paymtType === selectedPaymentOption.value)?.paymCond

  const showRiskForWinterSports = false
  const showRiskForCovid = false

  const travelersCount = userStore.adultsCount + userStore.childrenCount

  const onInclusiveCoverToggle = (isOpen: boolean, coverId: string, coverName: string) => {
    gaService.sendCoverToggleQuote({
      category: props.typeOfTravel,
      numOfTravelers: travelersCount,
      section: 'inclusive_cover_info',
      quoteId: quote.value?.quoteno || 0,
      totalPrice: totalPrice.value,
      coverName,
    })
  }

  const onTravellersExpand = () => {
    gaService.sendTravellersToggleQuote({
      category: props.typeOfTravel,
      numOfTravelers: travelersCount,
      quoteId: quote.value?.quoteno || 0,
      totalPrice: totalPrice.value,
    })
  }

  const sendCyberTooltipOpen = () => {
    gaService.sendCyberTooltip(
      props.typeOfTravel,
      'your_quote',
      t('addon.cyber.excess.info'),
      quote.value?.quoteno || 0
    )
  }

  const onChooseCoinsReward = useDebounceFn(async (option: PreferredCoinsReward) => {
    userStore.setPreferredReward(option, true)
    isPriceLoading.value = true

    try {
      await getTotalPrice()
      gaService.sendCoinsRewardTabClick(
        option,
        props.typeOfTravel,
        travelers.value.length,
        quote.value?.quoteno || 0,
        totalPrice.value,
        option === PreferredCoinsReward.SPEND
          ? toFixedNumber(selectedPlan.value.coinsDiscountValue ?? 0)
          : undefined
      )
    } finally {
      isPriceLoading.value = false
    }
  }, 100)

  onMounted(() => {
    emit(LOAD_EVENT_NAME, async () => {
      if (!(await getFormData(props.typeOfTravel, true))) return
      await Promise.all([getDestinations(), getWordings(), getTravelers(), getTravelQuoteUae()])
      await getTotalPrice()
      await getPaymentOptions()
      setAddonsPrice()
      setActiveAddons()
      setAddonsList()
      ;(withCovid.value && covidAddon.value ? numberAddons.value.push(covidAddon.value.id) : null,
        travelers.value.some((traveler) => traveler.winSports)
          ? numberAddons.value.push(winterSportExtension.value.id)
          : null)

      gaService.sendViewQuote(
        route.name,
        isOutbound.value
          ? userStore.destination.map((country) => country.id)
          : [userStore.residenceCountry.name],
        quoteStore.addonsList,
        quoteStore.activeAddons,
        travelers.value.length,
        addonsPrice.value,
        quote.value?.quoteno,
        totalPrice.value
      )

      await getAbTest(abTestKey.value)
      isInsuranceValuesQuote.value = INSURANCE_VALUES_SP_TEST_VALUES.includes(
        commonStore.getAbValue(abTestKey.value)
      )

      onPerformanceCheckEnd()
    })
  })
</script>

<template>
  <FlowCard :title="t('check_quote.header')" :with-button="false" class="flow-quote">
    <InformationCardInsuranceValues
      v-if="isInsuranceValuesQuote"
      :title="informationCardTitle"
      :description="destinationText"
      :options="informationCardOptions"
      variant="travel"
      class="flow-quote__information-card"
    />
    <InformationCard
      v-else
      :options="informationCardOptions"
      variant="travel"
      :title="informationCardTitle"
      class="flow-quote__information-card"
    >
      <template #icon>
        <TravelQuoteIcon />
      </template>
    </InformationCard>

    <FlowQuoteCoverage v-if="isInsuranceValuesQuote" :plan="selectedPlan" />
    <FlowAccordion
      v-else-if="selectedPlan"
      data-test="flow-quote"
      :plan="selectedPlan"
      :title="t('inbound_quote_inclusive_covers')"
      :with-addons="false"
      :type-of-travel="props.typeOfTravel"
      with-price
      is-quote-page
      @toggle="onInclusiveCoverToggle"
    />

    <BaseFoldingBlock class="flow-quote__card" @toggle="onTravellersExpand">
      <template #title>
        <div class="flow-quote__card-title">
          <div>{{ $t('travellers_insured') }}</div>
          <div class="flow-quote__travelers-edit">
            {{ travelersCountText }}
            <button data-test="flow-quote-card-edit" @click="onEditTravelers">
              <PencilIcon variant="dark" />
            </button>
          </div>
        </div>
      </template>
      <div v-for="traveler in travelers" :key="traveler.srNo" class="flow-quote__travelers-card">
        <div class="flow-quote__travelers-card-title">
          {{ traveler.firstName }} {{ traveler.lastName }}
        </div>
        <BaseChip
          v-if="traveler.srNo === 'holder'"
          variant="transparent"
          :text="t('check_quote.policy_holder.label')"
          data-test="policy-holder-chip"
        />
        <div class="flow-quote__travelers-card-content">
          <div v-if="traveler.srNo !== 'holder'" class="flow-quote__travelers-relation">
            {{ traveler.relationName }}
          </div>
          <div>
            <div>{{ $t('inbound_nationality_nationality') }}:</div>
            <div>{{ traveler.nationalityName }}</div>
          </div>
          <div>
            <div>{{ $t('inbound_id_number') }}:</div>
            <div>{{ traveler.passportNo }}</div>
          </div>
          <div>
            <div>{{ $t('inbound_dob_date') }}:</div>
            <div>{{ formatDate(traveler.dateOfBirth, locale) }}</div>
          </div>
          <div v-if="traveler.srNo === 'holder' && props.typeOfTravel === TravelDirection.OUTBOUND">
            <div>{{ $t('outbound.travelers.gender.label') }}:</div>
            <div>{{ traveler.gender }}</div>
          </div>
          <div v-if="traveler.srNo === 'holder' && props.typeOfTravel === TravelDirection.OUTBOUND">
            <div>{{ $t('outbound.travelers.qatar_id') }}:</div>
            <div>{{ traveler.emirateId }}</div>
          </div>
          <div v-if="traveler.srNo === 'holder' && props.typeOfTravel === TravelDirection.OUTBOUND">
            <div>{{ $t('outbound.personal_info.qid_exp_date') }}:</div>
            <div>{{ formatDate(traveler.civilIdExpiryDate, locale) }}</div>
          </div>

          <template v-if="traveler.srNo === 'holder'">
            <div>
              <div>{{ $t('inbound_travelers_add_email') }}:</div>
              <div>{{ userStore.email }}</div>
            </div>
            <div>
              <div>{{ $t('inbound_travelers_add_mobile_number') }}:</div>
              <div>{{ userStore.maskedPhone }}</div>
            </div>
          </template>
        </div>
      </div>
    </BaseFoldingBlock>

    <CoversBlock
      v-if="covidAddon?.value"
      :title="covidAddon.name"
      :desc="$t('outbound_covid_cover_description')"
      :risk="$t('check_quote.covid_cover.risks.annotation')"
      :is-price-loading="isPriceLoading"
      :show-risk="showRiskForCovid"
      :cover-limit="isInsuranceValuesQuote ? $t('check_quote.covid_cover.limit') : ''"
    >
      <template #price>
        <div class="flow-quote__cover-price-wrapper">
          <div class="ltr">{{ $t('qar') }} {{ toFixedNumber(covidAddon.value) }}</div>
          <div v-if="travelers.length > 1" class="flow-quote__cover-price-text">
            {{ $t('check_quote.people', travelers.length) }}
          </div>
        </div>
        <ToggleInput
          :model-value="withCovid"
          data-test="covid-toggle"
          @click.stop
          @update:model-value="onCovidChange"
        />
      </template>
    </CoversBlock>

    <CoversBlock
      v-if="winterSportExtensionPrice"
      :is-covid="false"
      :title="winterSportExtension.name"
      :desc="$t('outbound_sport_cover_winter_description')"
      :risk="$t('check_quote.winter_cover.risks.annotation')"
      :is-price-loading="isPriceLoading"
      :show-risk="showRiskForWinterSports"
      is-mobile-layout-equal-to-web
      :cover-limit="isInsuranceValuesQuote ? $t('check_quote.winter_cover.limit') : ''"
    >
      <template #price>
        <div class="flow-quote__cover-price-wrapper">
          <div class="ltr">{{ $t('qar') }} {{ winterSportPrice }}</div>
          <div v-if="travelers.length > 1" class="flow-quote__cover-price-text">
            {{ winterSportPriceText }}
          </div>
        </div>
      </template>
      <template #travelers>
        <div v-for="(traveler, i) in travelers" :key="i" class="flow-quote__cover-list">
          {{ traveler.firstName }} {{ traveler.lastName }}
          <ToggleInput
            v-model="traveler.winSports"
            :class="{ disabled: isPriceLoading }"
            :data-test="`winter-sports-toggle-${i}`"
            @click="
              $event.target.value &&
              onToggleAddon(winterSportExtension.id, $event.target.checked, traveler.srNo)
            "
          />
        </div>
      </template>
    </CoversBlock>

    <CoversBlock
      v-if="cyberAddon?.value"
      :title="$t('outbound.cyber_security_addon.name')"
      :is-price-loading="isPriceLoading"
    >
      <template #pre>
        <FingerprintLottie v-model="withCyber" data-test="fingerprint" />
      </template>
      <template #price>
        <span class="ltr">{{ $t('qar') }} {{ toFixedNumber(cyberAddon.value) }}</span>
        <ToggleInput
          :model-value="withCyber"
          data-test="cyber-toggle"
          @click.stop
          @update:model-value="onCyberChange"
        />
      </template>
      <template #body>
        <p class="flow-quote__cyber-desc">
          {{ $t('outbound.cyber_security_addon.desc_1') }}
          <strong>{{ $t('outbound.cyber_security_addon.desc_2') }}</strong>
          {{ $t('outbound.cyber_security_addon.desc_3') }}
          <CyberSecurityButton
            is-add-button-shown
            :quote-id="quoteStore.quoteNo"
            :cyber-state="withCyber"
            data-test="cyber-security-quote"
            @add-cyber-cover="onCyberChange(true)"
          />
        </p>

        <AddonTooltip
          :label="t('addon.cyber.excess.label')"
          :amount="t('addon.cyber.excess.amount')"
          :info="t('addon.cyber.excess.info')"
          @open-tooltip="sendCyberTooltipOpen"
        />
      </template>
    </CoversBlock>

    <OoredooBanner v-if="featuresStore.isOoredooEnabled" />
    <AiraloBanner v-else-if="showAiraloBanner" />

    <div class="flow-quote-payment">
      <div class="flow-quote-payment__block">
        <div
          v-if="userStore.promocode"
          class="flow-quote-payment__promocode flow-quote-payment__row flow-quote-payment__subtitle"
        >
          <div>{{ $t('checkout.payment_block.promo_code.title') }}</div>
          <div>{{ userStore.promocode }}</div>
        </div>
        <CoinsRewardOptions
          v-if="canApplyCoinsDiscount"
          :active-option="userStore.preferredReward"
          :coins-reward="quoteStore.productBonus"
          :coins-price="quoteStore.coinsDiscountPrice"
          :loading="isPriceLoading"
          @choose-reward="onChooseCoinsReward"
        />
        <VLoader v-if="isPriceLoading" />
        <template v-else>
          <div class="flow-quote-payment__inner-block">
            <div class="flow-quote-payment__row">
              {{ $t('checkout.payment_block.your_quote.title') }}
              <div class="ltr">
                {{ $t('qar') }} {{ toFixedNumber(selectedPlan?.price + selectedPlan?.discount) }}
              </div>
            </div>

            <div v-if="isOutbound && addonsPrice" class="flow-quote-payment__row">
              {{ $t('checkout.payment_block.addons.title') }}
              <div class="ltr">{{ $t('qar') }} {{ addonsPrice }}</div>
            </div>

            <div v-if="withCyber && cyberAddon" class="flow-quote-payment__row">
              {{ $t('outbound.cyber_security_addon.name') }}
              <div class="ltr">{{ $t('qar') }} {{ toFixedNumber(cyberAddon.value) }}</div>
            </div>
            <CoinsDiscount
              v-if="shouldApplyCoinsDiscount"
              :amount="toFixedNumber(selectedPlan.coinsDiscountValue)"
            />
          </div>

          <div
            v-if="selectedPlan?.discounts.length > 0"
            class="flow-quote-payment__inner-block flow-quote-payment__discount-block"
          >
            <div class="flow-quote-payment__subtitle">
              {{ $t('checkout.payment_block.discounts.title') }}
            </div>
            <template v-for="discount in selectedPlan?.discounts" :key="discount.name">
              <div
                v-if="discount.premium"
                class="flow-quote-payment__row flow-quote-payment__discount"
              >
                <div>{{ discount.name }}</div>
                <div class="ltr">
                  -{{ $t('qar') }} {{ toFixedNumber(Number(discount.premium)) }}
                </div>
              </div>
            </template>
          </div>

          <div class="flow-quote-payment__row flow-quote-payment__total">
            <div>{{ $t('checkout.payment_block.total.title') }}</div>
            <div class="ltr">{{ $t('qar') }} {{ toFixedNumber(totalPrice) }}</div>
          </div>
          <CoinsReward
            v-if="quoteStore.productBonus > 0 && !shouldApplyCoinsDiscount"
            :amount="quoteStore.productBonus"
          />
          <TicketBlock v-if="isRamadanCampaign" />
        </template>
      </div>

      <div class="flow-quote-payment__block">
        <p class="flow-quote-payment__block-title">
          {{ $t('checkout.payment_block.select_method.title') }}
        </p>
        <PaymentOptions
          v-if="paymentOptions.length"
          v-model="selectedPaymentOption"
          :options="paymentOptions"
          data-test="payment-options"
        />

        <div>
          <BaseCheckbox
            id="termsConditions"
            v-model="isTermsConditionsChecked"
            name="termsConditions"
            class="flow-quote-payment__checkbox"
            :class="{ 'flow-quote-payment__checkbox--error': checkboxErrors.agree }"
            data-test="terms-conditions-checkbox"
          >
            <div>
              {{ $t('checkout.payment_block.checkbox_confirmation.text') }}
              <a
                :href="termConditionsLink"
                :target="getLinkTarget()"
                @click="gaService.sendTermsConditions(route.name, quote?.quoteno)"
                data-test="terms-conditions-link"
              >
                {{ $t('checkout.payment_block.checkbox_confirmation.link') }}
              </a>
            </div>
          </BaseCheckbox>
        </div>

        <BaseButton v-show="!isApplePaySelected" big icon="none" @click="onBuy">
          {{ $t('qic_quote_checkout_button') }}
        </BaseButton>

        <ApplePayBtn
          v-if="hasApplePayOption"
          :is-active="isApplePaySelected"
          :quoteno="quote?.quoteno || ''"
          :price="totalPrice"
          :on-before-pay="getFinalQuotePrice"
          @succeed="onBuy"
          @error="onPaymentError"
        />

        <div class="flow-quote-payment__payment-terms">
          {{ $t('checkout.payment_block.confirmation.text') }}
          <span role="button" @click="onPaymentTermsClick">
            {{ $t('checkout.payment_block.confirmation.link') }}
          </span>
        </div>

        <BaseReminder icon="lock" class="quote-reminder">
          {{ $t('checkout.payment_block.security_informing.text') }}
        </BaseReminder>
      </div>
    </div>
    <QuotePaymentTermsPopup
      v-model:active="isPaymentTermsShown"
      :description="paymentDescriptionForPopup()"
      data-test="payment-terms-popup"
    />
    <QuoteClaimDocsPopup v-model:active="isClaimDocumentsListShown" data-test="claim-docs-popup" />
    <AlertContainer ref="alertContainerRef" />
  </FlowCard>
</template>

<style scoped lang="scss">
  @import './FlowQuoteWidget';
</style>

<style lang="scss">
  @import '@/shared/assets/styles/utils';

  .flow-quote {
    .base-card__title {
      margin-bottom: 0 !important;
    }

    .base-folding-block__title {
      width: 100%;
    }

    &__cover-price-wrapper {
      text-align: end;
    }

    &__cover-price-text {
      @include caption-7;
    }
  }

  .flow-quote__information-card {
    .card-option {
      color: #526c82cc;
    }
  }

  .flow-quote__card {
    .base-folding-block__summary {
      padding: 24px !important;

      @include mobile-flow {
        padding: 24px 16px !important;
      }
    }

    > .base-folding-block__summary:has(+ .base-folding-block__content) {
      border-bottom: 1px solid #526c8233;
    }

    .base-folding-block__content {
      padding-inline: 24px !important;
      display: grid;

      @include mobile-flow {
        padding-inline: 16px !important;
      }
    }
  }

  .flow-quote-payment__checkbox--error {
    color: #f94c27;

    &.checkbox {
      &:hover {
        label::before {
          border-color: #f94c27;
        }
      }

      label::before {
        border-color: #f94c27;
      }
    }

    & a {
      color: #f94c27 !important;
    }
  }

  .flow-quote-payment__checkbox {
    label {
      display: flex;
      gap: 16px;
      font-size: 14px;
      line-height: 24px;

      a {
        color: #526c82cc;
        text-decoration: underline;
      }
    }
  }

  .flow-quote-payment__block-title {
    @include caption-4-plus;

    @include mobile-flow {
      @include caption-5-plus;
    }
  }

  .quote-reminder {
    .reminder__text {
      @include caption-7;
    }
  }

  .flow-quote__cyber-desc {
    font-size: 14px;
    line-height: 24px;
    color: #1c1c1c;
  }

  .flow-quote_cyber-modal {
    color: #5927ff;
    cursor: pointer;
    background: none;
  }

  .rtl {
    .flow-quote__cover-price-wrapper {
      text-align: start;
    }
  }
</style>
